import { useEffect, useRef } from "react";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api, queryKeys, type Deletion } from "../../api";
import { useCan } from "../../auth/roles";
import { useToast } from "../../components/ui";

function deletedCopy(d: Deletion): string {
  if (d.kind === "app") return `${d.targetName || "The app"} was deleted.`;
  if (d.kind === "sessions") return d.targetName ? `Sessions in ${d.targetName} were deleted.` : "Sessions were deleted.";
  if (d.kind === "backend") return d.targetName ? `Backend events in ${d.targetName} were deleted.` : "Backend events were deleted.";
  return "Session deleted.";
}

/** Puts an accepted delete where the page can show it before the next refresh. */
export function rememberDeletion(qc: QueryClient, orgId: string, d: Deletion) {
  qc.setQueryData<Deletion[]>(queryKeys.deletions(orgId), (prev) => {
    const list = prev ?? [];
    if (list.some((row) => row.id === d.id)) return list.map((row) => (row.id === d.id ? d : row));
    return [d, ...list];
  });
}

/**
 * Watches deletes for this organization. A row that goes from running to done
 * is announced once, then the lists that held it refresh.
 */
export function useDeletions(orgId: string | undefined) {
  const can = useCan();
  const toast = useToast();
  const qc = useQueryClient();
  const seen = useRef(new Map<string, Deletion["status"]>());
  const ready = useRef(false);
  const q = useQuery({
    queryKey: queryKeys.deletions(orgId ?? ""),
    queryFn: () => api.orgs.deletions(orgId!),
    enabled: !!orgId && can.admin,
    refetchInterval: (query) => (query.state.data?.some((d) => d.status === "running") ? 1000 : false),
  });
  useEffect(() => {
    if (!q.isSuccess) return;
    const list = q.data ?? [];
    if (!ready.current) {
      for (const d of list) seen.current.set(d.id, d.status);
      ready.current = true;
      return;
    }
    for (const d of list) {
      const prev = seen.current.get(d.id);
      seen.current.set(d.id, d.status);
      if (prev !== "running") continue;
      if (d.status === "done") {
        toast.push("info", deletedCopy(d));
        void qc.invalidateQueries({ queryKey: queryKeys.projects(orgId!) });
        void qc.invalidateQueries({ queryKey: queryKeys.projectPicker(orgId!) });
        void qc.invalidateQueries({ queryKey: queryKeys.browserKeys(orgId!) });
        void qc.invalidateQueries({ queryKey: ["sessions"] });
        void qc.invalidateQueries({ queryKey: ["insights"] });
        void qc.invalidateQueries({ queryKey: ["backend"] });
      } else if (d.status === "failed") {
        toast.push("error", d.error || "Could not finish deleting.");
      }
    }
  }, [q.isSuccess, q.data, orgId, qc, toast]);
  return q;
}
